import { useRef } from "react";

import { type Scope, untrack } from "~/tools/impulse";

/**
 * A custom React hook that memoizes a value for the lifetime of the component,
 * similar to a "permanent" value that does not change between renders.
 *
 * The initializer function is called only once during the component's initial render,
 * and its result is stored permanently for the component's lifetime.
 * This is achieved using a ref, following React's documented pattern for stable initialization.
 *
 * @param init - A function that receives a `Scope` and returns the value to be memoized.
 * @returns The memoized value, which remains constant for the component's lifetime.
 *
 * @see https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents
 */
export function usePermanent<TValue>(init: (scope: Scope) => TValue): TValue {
	/**
	 * According to the official React documentation
	 * it is ok to write to the ref object during initialization.
	 *
	 * > Normally, writing or reading ref.current during render is not allowed.
	 * > However, it’s fine in this case because the result is always the same,
	 * > and the condition only executes during initialization so it’s fully predictable.
	 *
	 * @link https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents
	 */
	const ref = useRef<null | { value: TValue }>(null);

	ref.current ??= { value: untrack(init) };

	return ref.current.value;
}
